// Google Analytics 4 (GA4) 集成
// 测量ID: G-RBJS3M9DDR

/**
 * Strip the query string from a URL before it is logged or sent to analytics.
 *
 * Track Order deep links carry the shipping ZIP/postal code (?zipcode=...),
 * which is customer PII *and* the value the backend verifies. It must never be
 * sent to a third party or written to the console.
 *
 * @param {string} [url] - URL to sanitize; defaults to the current location
 * @returns {string} Origin + pathname only
 */
export const sanitizeTrackedUrl = (url) => {
  const raw = url || (typeof window !== 'undefined' ? window.location.href : '');
  if (!raw) {
    return '';
  }
  try {
    const parsed = new URL(raw, typeof window !== 'undefined' ? window.location.origin : undefined);
    return `${parsed.origin}${parsed.pathname}`;
  } catch (error) {
    // Fall back to a hard truncation rather than risking a leak.
    return String(raw).split('?')[0].split('#')[0];
  }
};

/**
 * 初始化 Google Analytics
 */
export const initGA = () => {
  const measurementId = 'G-RBJS3M9DDR';
  
  try {
    // 检查是否已经加载
    if (window.gtag) {
      console.log('Google Analytics already initialized');
      return;
    }

    // 加载 GA4 脚本
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    // 初始化 gtag
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;
    
    gtag('js', new Date());
    gtag('config', measurementId, {
      // 基础配置
      page_title: document.title,
      // Query string stripped: it can contain the order ZIP/postal code.
      page_location: sanitizeTrackedUrl(),
      // 隐私配置
      anonymize_ip: true,
      allow_google_signals: true,
      allow_ad_personalization_signals: false
    });
    
    console.log('📊 Google Analytics initialized with ID:', measurementId);
  } catch (error) {
    console.error('Failed to initialize Google Analytics:', error);
  }
};

/**
 * 记录页面访问
 * @param {string} page_title - 页面标题
 * @param {string} page_location - 页面URL（查询串会被剥离）
 * @param {Object} additionalData - 额外数据
 */
export const trackPageView = (page_title, page_location, additionalData = {}) => {
  if (typeof window.gtag !== 'undefined') {
    try {
      // Sanitize here too: callers must not be able to leak a ZIP by mistake.
      const safeLocation = sanitizeTrackedUrl(page_location);

      window.gtag('event', 'page_view', {
        page_title: page_title,
        page_location: safeLocation,
        ...additionalData
      });
      
      console.log('📊 Page view tracked:', { page_title, page_location: safeLocation, ...additionalData });
    } catch (error) {
      console.error('Failed to track page view:', error);
    }
  } else {
    console.warn('Google Analytics not initialized');
  }
};

/**
 * 记录自定义事件
 * @param {string} action - 事件动作
 * @param {Object} parameters - 事件参数
 */
export const trackEvent = (action, parameters = {}) => {
  if (typeof window.gtag !== 'undefined') {
    try {
      window.gtag('event', action, parameters);
      console.log('📊 Event tracked:', { action, ...parameters });
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  } else {
    console.warn('Google Analytics not initialized');
  }
};

// 其他跟踪函数已移除，只保留页面访问跟踪

// React Hook for page tracking
export const usePageTracking = () => {
  const trackPage = (pageName, additionalData = {}) => {
    // Query string stripped: Track Order links carry the order ZIP.
    trackPageView(pageName, sanitizeTrackedUrl(), additionalData);
  };
  
  const trackUserAction = (action, data = {}) => {
    trackEvent(action, {
      event_category: 'User Action',
      ...data
    });
  };
  
  return { trackPage, trackUserAction };
};

// 验证 GA 是否正常工作
export const testGA = () => {
  if (typeof window.gtag !== 'undefined') {
    trackEvent('ga_test', {
      event_category: 'Test',
      event_label: 'GA Test Event',
      test_time: new Date().toISOString()
    });
    console.log('✅ Google Analytics test event sent');
    return true;
  } else {
    console.error('❌ Google Analytics not available');
    return false;
  }
}; 